import type { Ref } from 'vue'

const CACHE_MS = 5 * 60 * 1000
const MAX_KEYS = 48
const LIMIT = 10

/** Una petición en curso por clave de caché (deduplica varias instancias). */
const inflight = new Map<string, Promise<void>>()

export type DestinationAutocompleteMenuItem = Autocomplete & {
  id?: number | null
  label?: string
  suffix?: string
  icon?: string
}

export type DestinationAutocompletePayload = { data: DestinationAutocompleteMenuItem[] }

type CacheEntry = {
  status: 'idle' | 'pending' | 'success' | 'error'
  data: DestinationAutocompletePayload | null
  error?: unknown
  fetchedAt: number
}

export function getDestinationAutocompleteCacheKey(locale: string, term: string, limit: number) {
  return `${locale}\u0000${limit}\u0000${term}`
}

function iconForDestinationType(type?: string) {
  if (type === 'hotel') return 'i-lucide-building-2'
  if (type === 'destination') return 'i-heroicons-map-pin-solid'
  if (type === 'place') return 'i-lucide-locate-fixed'
  return 'ic:round-near-me'
}

export function transformDestinationAutocompleteResponse(
  data: { data: Autocomplete[] }
): DestinationAutocompletePayload {
  const rows = data?.data ?? []
  return {
    data: rows.map((d) => ({
      id: d.element_id,
      label: d.name_translated,
      suffix: d.destination_name
        ? `${d.destination_name}, ${d.country_name_translated}`
        : d.country_name_translated,
      icon: iconForDestinationType(d.type),
      ...d,
    })),
  }
}

function patchStore(
  store: Ref<Record<string, CacheEntry>>,
  key: string,
  patch: Partial<CacheEntry>
) {
  const cur = store.value[key]
  store.value = {
    ...store.value,
    [key]: {
      status: 'idle',
      data: null,
      fetchedAt: 0,
      ...(cur ?? {}),
      ...patch,
    } as CacheEntry,
  }
}

/** Deja como máximo `MAX_KEYS` entradas (las más recientes por `fetchedAt`). */
function pruneOldest(store: Ref<Record<string, CacheEntry>>) {
  const pairs = Object.entries(store.value)
  if (pairs.length <= MAX_KEYS) return

  const kept = pairs
    .sort((a, b) => b[1].fetchedAt - a[1].fetchedAt)
    .slice(0, MAX_KEYS)

  store.value = Object.fromEntries(kept) as Record<string, CacheEntry>
}

/**
 * `/autocomplete`: caché cliente + dedupe in-flight; `refresh()` cuando haga falta (no al montar).
 */
export function useDestinationAutocompleteQuery(searchTermDebounced: Ref<string>) {
  const { locale } = useI18n()
  const runtimeConfig = useRuntimeConfig()

  const activeKey = computed(() =>
    getDestinationAutocompleteCacheKey(String(locale.value), searchTermDebounced.value, LIMIT)
  )

  const store = useState<Record<string, CacheEntry>>('byhours-destination-autocomplete-v1', () => ({}))

  const destinations = computed(() => store.value[activeKey.value]?.data ?? null)

  const pendingDest = computed(() => store.value[activeKey.value]?.status ?? 'idle')

  async function refresh() {
    if (import.meta.server) return

    const key = activeKey.value
    const loc = String(locale.value)
    const term = searchTermDebounced.value
    const base = runtimeConfig.public.API_CLIENT_BASE_URL as string

    const existing = store.value[key]
    const fresh =
      existing?.status === 'success' &&
      existing.data != null &&
      Date.now() - existing.fetchedAt < CACHE_MS

    if (fresh) return

    const snapshot = existing ?? { status: 'idle' as const, data: null, fetchedAt: 0 }

    if (!inflight.has(key)) {
      const work = (async () => {
        try {
          const raw = await $fetch<{ data: Autocomplete[] }>(`${base}/autocomplete`, {
            headers: { 'Content-Type': 'application/json' },
            query: { term, limit: LIMIT, locale: loc },
            cache: 'no-store',
          })
          patchStore(store, key, {
            status: 'success',
            data: transformDestinationAutocompleteResponse(raw),
            error: undefined,
            fetchedAt: Date.now(),
          })
          pruneOldest(store)
        } catch (err) {
          patchStore(store, key, {
            status: 'error',
            data: snapshot.data,
            error: err,
            fetchedAt: snapshot.fetchedAt,
          })
        }
      })().finally(() => {
        inflight.delete(key)
      })

      inflight.set(key, work)
    }

    patchStore(store, key, { ...snapshot, status: 'pending' })
    await inflight.get(key)
  }

  return { destinations, pendingDest, refresh }
}
